import { Dialog } from '@angular/cdk/dialog';
import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, inject, input, OnChanges, OnInit, output, SimpleChanges } from '@angular/core';
import { GameManager, gameManager } from 'src/app/game/businesslogic/GameManager';
import { GhsManager } from 'src/app/game/businesslogic/GhsManager';
import { SettingsManager, settingsManager } from 'src/app/game/businesslogic/SettingsManager';
import { ChallengeDeck } from 'src/app/game/model/data/Challenges';
import { GameState } from 'src/app/game/model/Game';
import { ChallengeCardComponent } from 'src/app/ui/figures/challenges/challenge-card';
import { ChallengeDeckDialogComponent } from 'src/app/ui/figures/challenges/challenge-deck-dialog';
import { ChallengeDeckFullscreenComponent } from 'src/app/ui/figures/challenges/challenge-deck-fullscreen';
import { GhsLabelDirective } from 'src/app/ui/helper/label';
import { PointerInputDirective } from 'src/app/ui/helper/pointer-input';
import { TrackUUIDPipe } from 'src/app/ui/helper/trackUUID';

export class ChallengeDeckChange {
  deck: ChallengeDeck;
  type: string;
  values: (string | number | boolean)[];

  constructor(deck: ChallengeDeck, type: string, ...values: (string | number | boolean)[]) {
    this.deck = deck;
    this.type = type;
    this.values = values;
  }
}

@Component({
  imports: [ChallengeCardComponent, GhsLabelDirective, NgClass, PointerInputDirective, TrackUUIDPipe],
  selector: 'ghs-challenge-deck',
  templateUrl: './challenge-deck.html',
  styleUrls: ['./challenge-deck.scss']
})
export class ChallengeDeckComponent implements OnInit, OnChanges {
  private element = inject(ElementRef);
  private dialog = inject(Dialog);
  private ghsManager = inject(GhsManager);

  private cdr = inject(ChangeDetectorRef);

  readonly inputChallengeDeck = input.required<ChallengeDeck>({ alias: 'deck' });
  get deck(): ChallengeDeck {
    return this.inputChallengeDeck();
  }

  readonly bottom = input<boolean>(false);
  readonly fullscreen = input<boolean>(true);
  readonly vertical = input<boolean>(false);
  readonly before = output<ChallengeDeckChange>();
  readonly after = output<ChallengeDeckChange>();
  readonly initTimeout = input<number>(1500);
  gameManager: GameManager = gameManager;
  settingsManager: SettingsManager = settingsManager;
  current: number = -1;
  internalDraw: number = -99;
  GameState = GameState;
  drawing: boolean = false;
  drawTimeout: any = null;
  queue: number = 0;
  queueTimeout: any = null;
  compact: boolean = false;
  disabled: boolean = false;
  drawDisabled: boolean = false;
  init: boolean = false;
  initServer: boolean = false;
  drawAvailable: number = 0;
  keepAvailable: number = 0;

  constructor() {
    this.ghsManager.uiChangeEffect((fromServer: boolean) => {
      this.update(fromServer);
    });
    this.element.nativeElement.addEventListener('pointerdown', (event: any) => {
      const elements = document.elementsFromPoint(event.clientX, event.clientY);
      if (elements[0].classList.contains('deck') && elements.length > 2) {
        (elements[2] as HTMLElement).click();
      }
    });
  }

  ngOnInit(): void {
    this.current = this.deck.current;
    this.internalDraw = -99;
    this.compact =
      this.deck.cards.length > 0 &&
      settingsManager.settings.automaticAttackModifierFullscreen &&
      settingsManager.settings.portraitMode &&
      (window.innerWidth < 800 || window.innerHeight < 400);

    const building = gameManager.game.party.buildings.find(
      (buildingModel) => buildingModel.name === 'town-hall' && buildingModel.level && buildingModel.state !== 'wrecked'
    );
    if (building) {
      this.drawAvailable = building.level;
      this.keepAvailable = Math.ceil(building.level / 2);
    }

    this.disabled =
      !gameManager.roundManager.firstRound ||
      gameManager.game.state === GameState.next ||
      !gameManager.game.scenario ||
      !this.deck.cards.length;
    this.drawDisabled = this.disabled || this.deck.current - this.deck.finished >= this.drawAvailable;

    if (!this.init) {
      this.drawTimeout = setTimeout(
        () => {
          this.current = this.deck.current;
          this.drawTimeout = null;
          this.init = true;
          this.cdr.markForCheck();
        },
        settingsManager.settings.animations ? this.initTimeout() * settingsManager.settings.animationSpeed : 0
      );
    }

    window.addEventListener('resize', () => {
      this.compact =
        this.deck.cards.length > 0 &&
        settingsManager.settings.automaticAttackModifierFullscreen &&
        settingsManager.settings.portraitMode &&
        (window.innerWidth < 800 || window.innerHeight < 400);
    });

    window.addEventListener('fullscreenchange', () => {
      this.compact =
        this.deck.cards.length > 0 &&
        settingsManager.settings.automaticAttackModifierFullscreen &&
        settingsManager.settings.portraitMode &&
        (window.innerWidth < 800 || window.innerHeight < 400);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['deck']) {
      this.update();
    }
  }

  update(fromServer: boolean = false) {
    const building = gameManager.game.party.buildings.find(
      (buildingModel) => buildingModel.name === 'town-hall' && buildingModel.level && buildingModel.state !== 'wrecked'
    );
    if (building) {
      this.drawAvailable = building.level;
      this.keepAvailable = Math.ceil(building.level / 2);
    }

    this.disabled =
      !gameManager.roundManager.firstRound ||
      gameManager.game.state === GameState.next ||
      !gameManager.game.scenario ||
      !this.deck.cards.length;
    this.drawDisabled = this.disabled || this.deck.current - this.deck.finished >= this.drawAvailable;

    if (this.initServer && gameManager.stateManager.wsState() !== WebSocket.OPEN) {
      this.initServer = false;
    }

    if (!this.deck.active) {
      if (this.queueTimeout) {
        clearTimeout(this.queueTimeout);
        this.queueTimeout = null;
      }
      this.queue = 0;
      this.drawing = false;
      this.current = this.deck.current;
      this.initServer = gameManager.stateManager.wsState() === WebSocket.OPEN;
    } else if (this.init && (!fromServer || this.initServer)) {
      if (this.current < this.deck.current) {
        const delta = this.deck.current - this.current;
        if (!fromServer && this.current === -1 && delta > 1) {
          this.current = this.deck.current;
        } else {
          this.queue = Math.max(0, delta);
          if (!this.queueTimeout) {
            this.queue--;
            this.current++;
            this.drawQueue(fromServer);
          }
        }
      } else if (!this.queueTimeout || this.deck.current < this.current + this.queue) {
        if (this.queueTimeout) {
          clearTimeout(this.queueTimeout);
          this.queueTimeout = null;
        }
        this.queue = 0;
        this.drawing = false;
        this.current = this.deck.current;
      }
    } else {
      this.current = this.deck.current;
      if (fromServer && !this.initServer) {
        this.initServer = true;
      }
    }

    this.compact =
      settingsManager.settings.automaticAttackModifierFullscreen &&
      settingsManager.settings.portraitMode &&
      (window.innerWidth < 800 || window.innerHeight < 400);
  }

  drawQueue(fromServer: boolean) {
    this.drawing = true;
    this.element.nativeElement.getElementsByClassName('deck')[0].classList.add('drawing');
    this.queueTimeout = setTimeout(
      () => {
        this.drawing = false;
        this.queueTimeout = null;

        if (this.queue > 0) {
          this.queue--;
          this.current++;
          this.drawQueue(fromServer);
        } else {
          this.element.nativeElement.getElementsByClassName('deck')[0].classList.remove('drawing');
          if (this.queue < 0) {
            this.queue = 0;
          }
        }
        this.cdr.markForCheck();
      },
      settingsManager.settings.animations ? 1050 * settingsManager.settings.animationSpeed : 0
    );
  }

  draw(event: any) {
    if (this.compact && this.fullscreen()) {
      this.openFullscreen(event);
    } else if (!this.drawDisabled && this.deck.cards.length > 0) {
      if (!this.drawTimeout && this.deck.current < this.deck.cards.length - (this.queue === 0 ? 0 : 1)) {
        this.drawTimeout = setTimeout(
          () => {
            this.before.emit(new ChallengeDeckChange(this.deck, 'challengeDeck.draw'));
            gameManager.challengesManager.drawCard(this.deck);
            this.after.emit(new ChallengeDeckChange(this.deck, 'challengeDeck.draw'));
            this.drawTimeout = null;
          },
          settingsManager.settings.animations ? 150 * settingsManager.settings.animationSpeed : 0
        );
      }
    } else {
      this.dialog.open(ChallengeDeckDialogComponent, {
        panelClass: ['dialog'],
        data: {
          deck: this.deck,
          before: this.before,
          after: this.after
        }
      });
    }
  }

  openFullscreen(event: any) {
    if (this.fullscreen()) {
      this.dialog.open(ChallengeDeckFullscreenComponent, {
        panelClass: ['fullscreen-panel'],
        backdropClass: ['fullscreen-backdrop'],
        data: {
          deck: this.deck,
          before: this.before,
          after: this.after
        }
      });

      event.preventDefault();
      event.stopPropagation();
    }
  }

  clickCard(index: number, event: any) {
    if (this.disabled) {
      this.open(event);
    } else if (index <= this.current) {
      this.before.emit(new ChallengeDeckChange(this.deck, 'challengeDeck.toggle', index));
      gameManager.challengesManager.toggleKeep(this.deck, index, this.keepAvailable);
      this.after.emit(new ChallengeDeckChange(this.deck, 'challengeDeck.toggle', index));
    }
  }

  doubleClickCard(index: number, event: any) {
    if (!this.drawing || index > this.current) {
      this.open(event);
    }
  }

  open(event: any) {
    if (
      this.deck.cards.length > 0 &&
      this.fullscreen() &&
      settingsManager.settings.automaticAttackModifierFullscreen &&
      settingsManager.settings.portraitMode &&
      (window.innerWidth < 800 || window.innerHeight < 400)
    ) {
      this.openFullscreen(event);
    } else {
      this.dialog.open(ChallengeDeckDialogComponent, {
        panelClass: ['dialog'],
        data: {
          deck: this.deck,
          before: this.before,
          after: this.after
        }
      });
    }
    event.preventDefault();
    event.stopPropagation();
  }
}
